/* ============================================================================
   EON 2Life — vanilla JS to power the Replo-exported home + product markup.
   Replaces the Replo runtime bundle. Handles: carousels, FAQ accordions,
   marquee, add-to-cart, entrance animations. No dependencies.

   Add-to-cart behavior verified against the live Replo output (2026-09-21):
   each page has exactly one hidden `<form data-type="add-to-cart-form"
   action="/cart/add">` with its `id`/`quantity`/`selling_plan` inputs already
   baked in by Liquid at render time (`{{ reploSelectedVariant.id }}` etc).
   Every `[data-replo-add-product-variant-to-cart]` button on that page
   submits that same form — that is the actual current behavior of the
   Replo-authored page (there is only one wired variant per page), not a
   simplification we are introducing.
   ============================================================================ */
(function () {
  'use strict';

  /* --------- 1) CAROUSELS  data-replo-carousel="true" --------- */
  function initCarousel(root) {
    var tracks = root.querySelectorAll('[data-replo-part="slide-track"]');
    if (!tracks.length) return;
    var primary = tracks[0];
    for (var t = 0; t < tracks.length; t++) {
      if (tracks[t].offsetParent !== null) { primary = tracks[t]; break; }
    }
    var slides = primary.querySelectorAll('[data-replo-part="slide"]:not([data-is-cloned="true"])');
    var prevBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
    var nextBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="next"]');
    var liveRegion = root.querySelector('.replo-sr-only');
    if (!slides.length) return;

    var idx = 0;
    var total = slides.length;

    function update() {
      var target = slides[idx];
      if (!target) return;
      var offset = target.offsetLeft - primary.offsetLeft;
      tracks.forEach(function (tr) {
        tr.style.transition = 'transform .45s ease';
        tr.style.transform = 'translate3d(' + (-offset) + 'px,0,0)';
      });
      if (liveRegion) liveRegion.textContent = 'Slide ' + (idx + 1) + ' of ' + total;
    }

    function go(delta) { idx = (idx + delta + total) % total; update(); }

    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); go(1); });

    var startX = null;
    primary.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    primary.addEventListener('touchend', function (e) {
      if (startX == null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) go(dx > 0 ? -1 : 1);
      startX = null;
    });

    window.addEventListener('resize', update);
    requestAnimationFrame(update);
  }

  /* --------- 2) FAQ ACCORDION  data-replo-collapsible="true" --------- */
  /* SSR panels ship as <div class="replo-animated" hidden>. Remove [hidden]
     on open, set --radix-collapsible-content-height before toggling so the
     bundle's own slideDown/slideUp CSS keyframes animate correctly. */
  function initCollapsible(root) {
    var trigger = root.querySelector('[aria-expanded]');
    if (!trigger) return;
    var panelId = trigger.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) panel = trigger.nextElementSibling;
    if (!panel) return;

    var open = trigger.getAttribute('aria-expanded') === 'true';
    var closeTimer = null;

    function toggle() {
      open = !open;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.setAttribute('data-state', open ? 'open' : 'closed');
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (open) {
        panel.removeAttribute('hidden');
        panel.style.setProperty('--radix-collapsible-content-height', panel.scrollHeight + 'px');
        panel.setAttribute('data-state', 'open');
      } else {
        panel.style.setProperty('--radix-collapsible-content-height', panel.scrollHeight + 'px');
        panel.setAttribute('data-state', 'closed');
        closeTimer = setTimeout(function () { panel.setAttribute('hidden', ''); }, 220);
      }
    }

    trigger.addEventListener('click', toggle);
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
  }

  /* --------- 3) MARQUEE  data-replo-marquee-root="true" --------- */
  function initMarquee(root) {
    var track = root.querySelector('[data-replo-marquee-track="true"]');
    if (track) track.style.animationPlayState = 'running';
  }

  /* --------- 4) ADD TO CART  [data-replo-add-product-variant-to-cart] --------- */
  function findCartForm(btn) {
    var container = btn.closest('[data-replo-product-container]');
    var scope = container || document;
    return scope.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]');
  }
  function initAddToCartButton(btn) {
    btn.addEventListener('click', async function (e) {
      e.preventDefault();
      var form = findCartForm(btn);
      if (!form) {
        console.warn('[EON] no add-to-cart form found for button', btn);
        return;
      }
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) {
        console.warn('[EON] add-to-cart form has no variant id', form);
        return;
      }
      btn.setAttribute('disabled', 'true');
      var fd = new FormData(form);
      try {
        await fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } });
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          throw new Error(err.description || err.message || ('cart add failed: ' + res.status));
        }
        window.location.href = '/cart';
      } catch (err) {
        console.error('[EON] add to cart error', err);
        btn.removeAttribute('disabled');
        alert('Não foi possível adicionar ao carrinho. Tente novamente.');
      }
    });
  }

  /* --------- 5) QUANTITY/PLAN SELECTOR  [role="radiogroup"] --------- */
  /* Cards are plain <div role="button"> siblings inside a role="radiogroup",
     each followed by a decorative aria-hidden radio (the one with `checked`
     marks the SSR default). No real Shopify variant id is baked onto the
     card itself, so we resolve it by matching the card's displayed price
     against this product's real variants — loaded from the `replo-deps-products`
     JSON block Liquid renders on the page (product.id/variants with real
     prices). Matching is scoped to the current product and only applied when
     exactly one variant's price matches (never guess on ambiguity). */
  function getReploProducts() {
    var script = document.getElementById('replo-deps-products');
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch (e) {
      console.error('[EON] failed to parse replo-deps-products', e);
      return [];
    }
  }
  function parseBRLNumbers(text) {
    var out = [];
    var re = /R\$\s?([\d.,]+)/g;
    var m;
    while ((m = re.exec(text)) !== null) {
      var n = parseFloat(m[1].replace(/\./g, '').replace(',', '.'));
      if (!isNaN(n)) out.push(n);
    }
    return out;
  }
  function resolveCardVariant(card, variants) {
    var candidates = parseBRLNumbers(card.textContent || '');
    if (!candidates.length || !variants.length) return null;
    var matches = [];
    variants.forEach(function (v) {
      /* Liquid's `product | json` serializes money fields as integer cents
         (e.g. 9600 for R$96,00) — not the decimal string the Admin API uses. */
      var vp = parseFloat(v.price) / 100;
      if (candidates.some(function (n) { return Math.abs(n - vp) < 0.01; })) matches.push(v);
    });
    var uniqueIds = [];
    matches.forEach(function (v) { if (uniqueIds.indexOf(v.id) === -1) uniqueIds.push(v.id); });
    return uniqueIds.length === 1 ? matches[0] : null;
  }
  function initRadioGroup(group) {
    var container = group.closest('[data-replo-product-container]');
    if (!container) return;
    var handle = container.getAttribute('data-replo-product-handle');
    var entry = getReploProducts().filter(function (p) { return p.handle === handle; })[0];
    var variants = (entry && entry.data && entry.data.variants) || [];
    if (!variants.length) return;

    var form = container.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    var spInput = form.querySelector('input[name="selling_plan"]');
    if (!idInput) return;

    var cards = [];
    for (var i = 0; i < group.children.length; i++) {
      var el = group.children[i];
      if (el.getAttribute('role') === 'button') cards.push(el);
    }
    if (!cards.length) return;

    /* Replo bakes the "selected" look as a permanent CSS class on whichever
       card was selected at render time — it never gets removed by a class
       swap, so we must override it with an inline style. Read the ACTUAL
       border Replo baked for the selected card before touching anything, so
       our own indicator matches this group's real design instead of a
       hardcoded guess, then apply/clear that same inline border on whichever
       card is selected (inline style always wins over the class rule). */
    var selectedBorder = '2px solid #1c1c1c';
    cards.forEach(function (c) {
      var radio = c.nextElementSibling;
      if (radio && radio.matches && radio.matches('input[type="radio"]') && radio.checked) {
        var cs = getComputedStyle(c);
        if (parseFloat(cs.borderTopWidth) > 0) {
          selectedBorder = cs.borderTopWidth + ' ' + cs.borderTopStyle + ' ' + cs.borderTopColor;
        }
      }
    });

    function applyVisual(card, isSelected) {
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      card.style.border = isSelected ? selectedBorder : 'none';
    }

    function select(card) {
      var variant = resolveCardVariant(card, variants);
      if (!variant) {
        console.warn('[EON] could not resolve a real variant for radio card', card);
        return;
      }
      cards.forEach(function (c) { applyVisual(c, c === card); });
      idInput.value = variant.id;
      if (spInput) spInput.value = '';
    }

    var defaultCard = null;
    cards.forEach(function (card) {
      var radio = card.nextElementSibling;
      if (radio && radio.matches && radio.matches('input[type="radio"]') && radio.checked) {
        defaultCard = card;
      }
      card.addEventListener('click', function () { select(card); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(card); }
      });
    });
    if (defaultCard) select(defaultCard);
    else cards.forEach(function (c) { applyVisual(c, false); });
  }

  /* --------- 6) INERT CTAS (no data-replo action configured) --------- */
  /* Some <button>s in the Replo export (e.g. the hero "COMPRAR AGORA") carry
     no data-replo-add-product-variant-to-cart, no href, nothing — they are
     inert on the live Replo page too (confirmed: same pattern documented in
     the Livera migration). Deliberate deviation: smooth-scroll to the wired
     offer/buybox section instead of leaving them dead. */
  function initInertCta(btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var target = document.querySelector('[data-replo-product-container]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* --------- 7) ENTRANCE ANIMATIONS (alchemy-fade / alchemy-slide-y / ...) --------- */
  /* Replo bakes entrance animations as paused CSS animations with
     animation-fill-mode:both — without the runtime, elements are frozen
     INVISIBLE at the `from` keyframe. Play the animation once the element
     scrolls into view; marquee tickers run immediately. */
  function initEntranceAnimations() {
    var entrance = [];
    document.querySelectorAll('#replo-fullpage-element *').forEach(function (el) {
      var cs = getComputedStyle(el);
      var name = cs.animationName || '';
      if (name.indexOf('alchemy-') === -1) return;
      if (cs.animationPlayState.indexOf('paused') === -1) return;
      if (name.indexOf('marquee') !== -1) {
        el.style.animationPlayState = 'running';
        return;
      }
      entrance.push(el);
    });
    if (!('IntersectionObserver' in window)) {
      entrance.forEach(function (el) { el.style.animationPlayState = 'running'; });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.style.animationPlayState = 'running';
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.1 });
    entrance.forEach(function (el) { io.observe(el); });
  }

  /* --------- 8) IMAGE LIGHTBOX  picture[role="button"] --------- */
  /* Replo marks gallery thumbnails as role="button" tabindex="0" but the
     click behavior itself (expand to full size) lived in Replo's own
     runtime, which we replaced. Never implemented here until now — clicking
     a thumbnail did nothing. Opens the clicked image at full size in a
     simple overlay; closes on click, Escape, or another click. */
  function initImageLightbox(pic) {
    function open(e) {
      if (e) e.preventDefault();
      var img = pic.querySelector('img');
      if (!img) return;
      var overlay = document.createElement('div');
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.85);display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;cursor:zoom-out;';
      var big = document.createElement('img');
      big.src = img.currentSrc || img.src;
      big.alt = img.alt || '';
      big.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;';
      overlay.appendChild(big);
      function close() {
        overlay.removeEventListener('click', close);
        document.removeEventListener('keydown', onKey);
        overlay.remove();
      }
      function onKey(ev) { if (ev.key === 'Escape') close(); }
      overlay.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      document.body.appendChild(overlay);
    }
    pic.addEventListener('click', open);
    pic.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
  }

  /* --------- BOOT --------- */
  /* Each init wrapped individually: an error on one carousel/accordion must
     never stop the rest of boot() (including the add-to-cart buttons) from
     running — a forEach abandons all remaining iterations on first throw. */
  function safeEach(nodeList, fn, label) {
    nodeList.forEach(function (el) {
      try { fn(el); } catch (err) { console.error('[EON] ' + label + ' init error', err, el); }
    });
  }
  function boot() {
    safeEach(document.querySelectorAll('[data-replo-carousel="true"]'), initCarousel, 'carousel');
    safeEach(document.querySelectorAll('[data-replo-collapsible="true"]'), initCollapsible, 'collapsible');
    safeEach(document.querySelectorAll('[data-replo-marquee-root="true"]'), initMarquee, 'marquee');
    safeEach(document.querySelectorAll('[role="radiogroup"]'), initRadioGroup, 'radio-group');
    safeEach(document.querySelectorAll('[data-replo-add-product-variant-to-cart]'), initAddToCartButton, 'add-to-cart');
    safeEach(
      document.querySelectorAll('#replo-fullpage-element button:not([data-replo-add-product-variant-to-cart]):not([data-replo-part="nav-button"])'),
      initInertCta,
      'inert-cta'
    );
    safeEach(document.querySelectorAll('#replo-fullpage-element picture[role="button"]'), initImageLightbox, 'image-lightbox');
    /* This walks every element on the page (querySelectorAll('#replo-fullpage-element *')
       + getComputedStyle per element) to find alchemy-* entrance animations — on pages
       with thousands of nodes that's a measurable main-thread block right at the moment
       the browser wants to paint. Neither the home nor product page actually references
       an alchemy-* animation-name in their compiled CSS (verified), so this scan currently
       finds nothing on either page; deferring it costs nothing today and keeps the feature
       working if a future page/section does use it. */
    var deferIdle = window.requestIdleCallback || function (fn) { setTimeout(fn, 200); };
    deferIdle(function () {
      try { initEntranceAnimations(); } catch (err) { console.error('[EON] entrance animations init error', err); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
